//@ts-nocheck
import { useEffect, useState, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { TextField, Button, Grid, Autocomplete, CircularProgress } from '@mui/material';
import { BaseModal } from '../../molecules';
import {
  useAddResponseCode,
  useUpdateResponseCode,
  IResponseCode,
  useGetResourceList,
  useGetMemberVPosList,
} from '../../../hooks';
import { useSetSnackBar } from '../../../store/Snackbar.state';

interface ResponseCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  responseCode?: IResponseCode;
  refetch: () => void;
}

const schema = z.object({
  integrationCode: z.string().nonempty('Banka Adı zorunludur'),
  errorCode: z.string().nonempty('Hata Kodu zorunludur'),
  errorCodeDesc: z.string().nonempty('Hata Kodu Açıklaması zorunludur'),
  internalErrorCode: z.string().nonempty('İç Hata Kodu zorunludur'),
  internalErrorCodeDesc: z.string().nonempty('İç Hata Kodu Açıklaması zorunludur'),
});

export const ResponseCodeModal = ({ isOpen, onClose, responseCode, refetch }: ResponseCodeModalProps) => {
  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<IResponseCode>({
    resolver: zodResolver(schema),
    defaultValues: responseCode || {},
  });

  const setSnackbar = useSetSnackBar();
  const { mutate: addResponseCode, isLoading: isAdding } = useAddResponseCode();
  const { mutate: updateResponseCode, isLoading: isUpdating } = useUpdateResponseCode();

  const [options, setOptions] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fetchTimeout = useRef<NodeJS.Timeout>();
  const { mutate: getResourceList } = useGetResourceList();
  const [bankList, setBankList] = useState<any[]>([]);
  const { mutate: getMemberVPosList } = useGetMemberVPosList();

  useEffect(() => {
    if (isOpen) {
      getMemberVPosList(
        {
          orderBy: "CreateDate",
          orderByDesc: true,
          status: "ACTIVE",
          size: -1,
        },
        {
          onSuccess: (data) => {
            const list = data?.data?.map(
              (bank: { bankCode: string; bankName: string }) => {
                return {
                  label: bank.bankName,
                  value: bank.bankCode,
                };
              }
            );
            setBankList(list || []);
          },
        }
      );
    }
  }, [isOpen, getMemberVPosList]);

  const fetchOptions = useCallback((currentPage: any, currentInput: any) => {
    if (currentInput.length > 0 && currentInput.length < 3) {
      return;
    }
    setIsLoading(true);
    const params: any = {
      page: currentPage,
      size: 50,
      orderBy: 'CreateDate',
      orderByDesc: false
    };
    if (currentInput.length == 5) {
      params.Key = currentInput;
    } else if (currentInput.length > 5) {
      params.value = currentInput;
    }

    getResourceList(params, {
      onSuccess: (data: any) => {
        const newCodes = data?.data?.result?.map((code: { key: string; value: string }) => ({
          label: `${code.key} (${code.value})`,
          value: code.key,
        })) || [];

        setOptions(prev => currentPage === 0 ? newCodes : [...prev, ...newCodes]);
        setHasNextPage(newCodes.length === 50);
        setIsLoading(false);
      },
      onError: () => setIsLoading(false)
    });
  }, [getResourceList]);

  useEffect(() => {
    if (isOpen) {
      fetchOptions(0, '');
    } else {
      setOptions([]);
      setPage(0);
      setInputValue('');
      setHasNextPage(true);
    }
  }, [isOpen, fetchOptions]);

  const handleInputChange = (event: any, newInputValue: any, reason: any) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      clearTimeout(fetchTimeout.current);
      fetchTimeout.current = setTimeout(() => {
        setPage(0);
        fetchOptions(0, newInputValue);
      }, 500);
    }
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1) {
      if (hasNextPage && !isLoading) {
        const newPage = page + 1;
        setPage(newPage);
        fetchOptions(newPage, inputValue);
      }
    }
  };

  useEffect(() => {
    if (responseCode) {
      reset(responseCode);
    } else {
      reset({
        integrationCode: '',
        errorCode: '',
        errorCodeDesc: '',
        internalErrorCode: '',
        internalErrorCodeDesc: '',
      });
    }
  }, [responseCode, reset]);

  const onSubmit = (data: IResponseCode) => {
    const mutation = responseCode ? updateResponseCode : addResponseCode;

    if (responseCode) {
      data.id = responseCode.id;
    }

    mutation(data, {
      onSuccess: (res) => {
        if (res.isSuccess) {
          setSnackbar({ severity: 'success', isOpen: true, description: res.message });
          refetch();
          onClose();
        } else {
          setSnackbar({ severity: 'error', isOpen: true, description: res.message });
        }
      },
      onError: () => {
        setSnackbar({ severity: 'error', isOpen: true, description: 'İşlem sırasında bir hata oluştu' });
      },
    });
  };

  return (
    <BaseModal title={responseCode ? 'Hata Kodu Düzenle' : 'Yeni Hata Kodu Ekle'} isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="integrationCode"
              control={control}
              render={({ field: { onChange, value } }) => {
                const selectedBank = bankList.find(
                  (option: any) => option.value === value
                );
                return (
                  <Autocomplete
                    id="integrationCode"
                    options={bankList}
                    getOptionLabel={(option) => option.label}
                    value={selectedBank || null}
                    onChange={(_, newValue) => {
                      onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Banka Adı"
                        fullWidth
                        error={!!errors.integrationCode}
                        helperText={errors.integrationCode?.message}
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="errorCode"
              control={control}
              render={({ field }) => <TextField {...field} label="Hata Kodu" fullWidth error={!!errors.errorCode} helperText={errors.errorCode?.message} />}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="errorCodeDesc"
              control={control}
              render={({ field }) => <TextField {...field} label="Hata Kodu Açıklaması" fullWidth error={!!errors.errorCodeDesc} helperText={errors.errorCodeDesc?.message} />}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="internalErrorCode"
              control={control}
              render={({ field: { onChange, value } }) => {
                const foundOption = options.find(option => option.value === value);
                return (
                  <Autocomplete
                    freeSolo
                    options={options}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.label || '';
                    }}
                    value={foundOption || value || null}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'string') {
                        onChange(newValue);
                        setValue('internalErrorCodeDesc', '');
                      } else if (newValue) {
                        onChange(newValue.value);
                        const desc = newValue.label.substring(newValue.label.indexOf('(') + 1, newValue.label.lastIndexOf(')'));
                        setValue('internalErrorCodeDesc', desc);
                      } else {
                        onChange('');
                        setValue('internalErrorCodeDesc', '');
                      }
                    }}
                    onInputChange={handleInputChange}
                    loading={isLoading}
                    ListboxProps={{
                      onScroll: handleScroll,
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="İç Hata Kodu"
                        fullWidth
                        error={!!errors.internalErrorCode}
                        helperText={errors.internalErrorCode?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="internalErrorCodeDesc"
              control={control}
              disabled
              render={({ field }) => <TextField disabled {...field} label="İç Hata Kodu Açıklaması" fullWidth error={!!errors.internalErrorCodeDesc} helperText={errors.internalErrorCodeDesc?.message} />}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" disabled={isAdding || isUpdating}>
              {responseCode ? 'Güncelle' : 'Ekle'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseModal>
  );
};
